import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import { BrandingProvider } from './context/BrandingContext';
import { NotificationProvider } from './context/NotificationContext';
import { ThemeProvider } from './context/ThemeContext';
import { SidebarProvider } from './context/SidebarContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import CustomerDashboard from './pages/CustomerDashboard';
import AllTickets from './pages/All_Ticjet';
import EmployeeDashboard from './pages/EmployeeDashboard';
import Dashboard from './pages/Dashboard';
import CreateTicket from './pages/CreateTicket';
import TicketDetail from './pages/TicketDetail';
import KnowledgeBase from './pages/KnowledgeBase';
import KnowledgeSearch from './pages/KnowledgeSearch';
import KnowledgeFaqs from './pages/KnowledgeFaqs';
import KnowledgeFavorites from './pages/KnowledgeFavorites';
import MyArticles from './pages/MyArticles';
import ServiceCatalog from './pages/ServiceCatalog';
import Notifications from './pages/Notifications';
import MyIncidents from './pages/MyIncidents';
import UnassignedTickets from './pages/UnassignedTickets';
import PendingTickets from './pages/PendingTickets';
import ResolvedTickets from './pages/ResolvedTickets';
import ClosedTickets from './pages/ClosedTickets';
import FavoriteTickets from './pages/FavoriteTickets';
import RecentTickets from './pages/RecentTickets';
import CustomerOpenTickets from './pages/CustomerOpenTickets';
import CustomerResolvedTickets from './pages/CustomerResolvedTickets';
import CustomerClosedTickets from './pages/CustomerClosedTickets';
import CustomerFavoriteTickets from './pages/CustomerFavoriteTickets';
import ComingSoon from './pages/ComingSoon';
import NotFound from './pages/NotFound';
import Profile from './pages/Profile';
import UserManagement from './pages/UserManagement';
import DomainSettings from './pages/DomainSettings';
import Reports from './pages/Reports';
import Help from './pages/Help';
import SuperAdminTenants from './pages/SuperAdminTenants';
import SuperAdminTenantForm from './pages/SuperAdminTenantForm';
import SuperAdminTenantView from './pages/SuperAdminTenantView';
import SuperAdminGlobalTickets from './pages/SuperAdminGlobalTickets';
import SuperAdminLogs from './pages/SuperAdminLogs';
import SuperAdminAssignmentGroups from './pages/SuperAdminAssignmentGroups';
import SystemLogReport from './pages/SystemLogReport';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrandingProvider>
          <NotificationProvider>
            <SidebarProvider>
            <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />

          {/* Protected Routes */}
          <Route
            path="/customer/dashboard"
            element={
              <ProtectedRoute allowedRoles={['customer']}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/dashboard/old"
            element={
              <ProtectedRoute allowedRoles={['customer']}>
                <CustomerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/tickets"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminGlobalTickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/logs"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/logs/report"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SystemLogReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/assignment-groups"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminAssignmentGroups />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/tenants"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminTenants />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/tenants/new"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminTenantForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/tenants/:tenantId"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminTenantView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/tenants/:tenantId/edit"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminTenantForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin', 'global_support']}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/dashboard"
            element={
              <ProtectedRoute allowedRoles={['employee']}>
                <EmployeeDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/dashboard/old"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <AllTickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tickets"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee', 'global_support']}>
                <AllTickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets/create"
            element={
              <ProtectedRoute allowedRoles={['customer', 'admin', 'employee']}>
                <CreateTicket />
              </ProtectedRoute>
            }
          />

          {/* Incident Management Routes (Admin/Employee) - Must come before /tickets/:id */}
          <Route
            path="/admin/tickets/my"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <MyIncidents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tickets/unassigned"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <UnassignedTickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tickets/pending"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <PendingTickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tickets/resolved"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <ResolvedTickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tickets/closed"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <ClosedTickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tickets/favorites"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <FavoriteTickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tickets/recent"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <RecentTickets />
              </ProtectedRoute>
            }
          />

          {/* Customer Ticket Routes - Must come before /tickets/:id */}
          <Route
            path="/customer/tickets"
            element={
              <ProtectedRoute allowedRoles={['customer']}>
                <CustomerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/tickets/open"
            element={
              <ProtectedRoute allowedRoles={['customer']}>
                <CustomerOpenTickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/tickets/resolved"
            element={
              <ProtectedRoute allowedRoles={['customer']}>
                <CustomerResolvedTickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/tickets/closed"
            element={
              <ProtectedRoute allowedRoles={['customer']}>
                <CustomerClosedTickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/tickets/favorites"
            element={
              <ProtectedRoute allowedRoles={['customer']}>
                <CustomerFavoriteTickets />
              </ProtectedRoute>
            }
          />

          <Route
            path="/tickets/:id"
            element={
              <ProtectedRoute allowedRoles={['customer', 'admin', 'employee', 'super_admin']}>
                <TicketDetail />
              </ProtectedRoute>
            }
          />

          {/* Knowledge Base Routes */}
          <Route
            path="/customer/knowledge"
            element={
              <ProtectedRoute allowedRoles={['customer', 'admin', 'employee']}>
                <KnowledgeBase />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/knowledge/search"
            element={
              <ProtectedRoute allowedRoles={['customer', 'admin', 'employee']}>
                <KnowledgeSearch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/knowledge/faqs"
            element={
              <ProtectedRoute allowedRoles={['customer', 'admin', 'employee']}>
                <KnowledgeFaqs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/knowledge/favorites"
            element={
              <ProtectedRoute allowedRoles={['customer', 'admin', 'employee']}>
                <KnowledgeFavorites />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/knowledge"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <KnowledgeBase />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/knowledge/search"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <KnowledgeSearch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/knowledge/faqs"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <KnowledgeFaqs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/knowledge/favorites"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <KnowledgeFavorites />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/knowledge/my-articles"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <MyArticles />
              </ProtectedRoute>
            }
          />

          {/* Service Catalog Routes */}
          <Route
            path="/customer/service-items"
            element={
              <ProtectedRoute allowedRoles={['customer', 'admin', 'employee']}>
                <ServiceCatalog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/service-items"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <ComingSoon />
              </ProtectedRoute>
            }
          />

          {/* Reports & Analytics Routes */}
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee', 'customer', 'global_support']}>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/reports"
            element={
              <ProtectedRoute allowedRoles={['customer']}>
                <Reports />
              </ProtectedRoute>
            }
          />

          {/* Service Request Routes - Coming Soon (commented out for later) */}
          {/* <Route
            path="/admin/requests"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <ComingSoon />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/requests/approved"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <ComingSoon />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/requests/rejected"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <ComingSoon />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/requests/pending"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <ComingSoon />
              </ProtectedRoute>
            }
          /> */}

          {/* Categories Route - Coming Soon */}
          <Route
            path="/admin/categories"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <ComingSoon />
              </ProtectedRoute>
            }
          />

          {/* Notifications Routes */}
          <Route
            path="/customer/notifications"
            element={
              <ProtectedRoute allowedRoles={['customer', 'admin', 'employee']}>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee', 'global_support']}>
                <Notifications />
              </ProtectedRoute>
            }
          />

          {/* Profile Route - All authenticated users */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute allowedRoles={['customer', 'admin', 'employee', 'global_support', 'super_admin']}>
                <Profile />
              </ProtectedRoute>
            }
          />

          {/* User Management Route - Admin only */}
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <UserManagement />
              </ProtectedRoute>
            }
          />

          {/* Domain Access Control - Admin only */}
          <Route
            path="/admin/domains"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <DomainSettings />
              </ProtectedRoute>
            }
          />

          {/* Help & Support Route - All authenticated users */}
          <Route
            path="/help"
            element={
              <ProtectedRoute allowedRoles={['customer', 'admin', 'employee', 'global_support', 'super_admin']}>
                <Help />
              </ProtectedRoute>
            }
          />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Toaster position="top-right" richColors />
            </BrowserRouter>
            </SidebarProvider>
          </NotificationProvider>
        </BrandingProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;

